import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import {
    fetchProjectIssuesSuccess,
    showProjectIssueMessage,
    onSaveProjectIssueSuccess,
    onDeleteProjectIssueSuccess,
    addFavouriteProjectIssueSuccess,
    onSaveProjectIssueCacheSuccess,
} from 'actions/projectIssue';
import {
    ON_SAVE_PROJECT_ISSUE,
    ON_SAVE_JIRA_PROJECT_ISSUE,
    FETCH_ALL_PROJECT_ISSUE,
    ON_DELETE_PROJECT_ISSUE,
    ADD_FAVOURITE_PROJECT_ISSUE,
} from 'constants/ActionTypes';
import { fetchDataRequest, addOrUpdateDataRequest, deleteDataRequest } from 'services/ApiService';
import { apiErrorMessageFormatter } from 'services/errorService';
import { projectIssueCacheExpired, sanitizeProjectIssueCache } from 'services/projectIssueService';
import { ORGID_PATH_PARAM, PROJECTID_PATH_PARAM } from 'constants/api';
import { findIndex } from 'lodash';

const currentOrg = (state) => state.orgs.selectedOrg || {};
const getProjectIssueList = (state) => state.projectIssues.allProjectIssue || [];
const getProjectIssueCacheList = (state) => state.projectIssues.projectIssueCacheList || [];
const getSelectedProject = (state) => state.projects.selectedProject || {};

/**
 * On going to the dashboard - check if the project issue data is stale (they have the option refresh) - if so re-retrieve
 * @param {boolean} param0 - use cache which is true or false to use the cache or direct retrieve from api 
 */
export function* fetchProjectIssues({ payload }) {
    try {
        const useCache = payload; // for readability - dont judge me :->
        const selectedOrg = yield select(currentOrg);
        const selectedProject = yield select(getSelectedProject);
        const allProjectCacheIssues = yield select(getProjectIssueCacheList);
        // clean up the cache to remove stale items
        const sanitizedCacheIssues = yield call(sanitizeProjectIssueCache, allProjectCacheIssues);
        const cacheEntry = sanitizedCacheIssues.find(entry => entry?.id === selectedProject?.id);
        const now = new Date();
        // TODO - do we check project issue metadata to see if < 500 if so forget the cache and retrieve every time ?
        let fetchProjectIssues = false;
        let fetchedProjectIssues = {};
        let replaceCachedEntry = false;
        let addCachedEntry = false;
        if (cacheEntry) {
            // have a cache entry but still check the refresh time on the entry to determine if it has expired
            // {id: 1, lastUpdated: "datetime", issues: []}
            if (useCache) {
                if (projectIssueCacheExpired(cacheEntry?.lastUpdated)) {
                    // entry has expired so retrieve and set
                    fetchProjectIssues = true;
                    replaceCachedEntry = true;
                } else {
                    // hasnt expired so just set the selected project issue list
                    yield put(fetchProjectIssuesSuccess(cacheEntry?.issueData));
                }
            } else {
                // have cache entry but force refresh so retrieve and set
                fetchProjectIssues = true;
                replaceCachedEntry = true;
            }
        } else {
            // no cache entry so fetch and add to cache
            fetchProjectIssues = true;
            addCachedEntry = true;
        }
        if (fetchProjectIssues) {
            const importProjectId = payload?.jiraProjectAttributes?.key;
            fetchedProjectIssues = yield call(
                fetchDataRequest,
                'projectIssues',
                [ORGID_PATH_PARAM, PROJECTID_PATH_PARAM],
                // [selectedOrg?.id, payload?.jiraProjectAttributes?.key]
                [selectedOrg?.id, importProjectId]) || [];
            yield put(fetchProjectIssuesSuccess(fetchedProjectIssues));
        }
        if (replaceCachedEntry) {
            const index = findIndex(sanitizedCacheIssues, { id: cacheEntry?.id });
            const newCacheEntry = { id: cacheEntry?.id, lastUpdated: now, issueData: fetchedProjectIssues };
            sanitizedCacheIssues.splice(index, 1, newCacheEntry);
            yield put(onSaveProjectIssueCacheSuccess(sanitizedCacheIssues));
        } else if (addCachedEntry) {
            const newCacheEntry = { id: selectedProject?.id, lastUpdated: now, issueData: fetchedProjectIssues };
            sanitizedCacheIssues.push(newCacheEntry);
            yield put(onSaveProjectIssueCacheSuccess(sanitizedCacheIssues));
        }
    } catch (error) {
        yield put(showProjectIssueMessage(apiErrorMessageFormatter(error)));
    }
}

export function* deleteProjectIssue({ payload }) {
    try {
        // payload should be the projectIssueId
        const selectedOrg = yield select(currentOrg);
        yield call(
            deleteDataRequest,
            'deleteProjectIssue',
            'Deleting project issue '.concat(payload),
            [ORGID_PATH_PARAM, 'projectIssueId'],
            [selectedOrg?.id, payload]
        ) || [];
        yield put(onDeleteProjectIssueSuccess(payload)); // just dispatch the id as the reducer will remove from the item from the list
    } catch (error) {
        yield put(showProjectIssueMessage(apiErrorMessageFormatter(error)));
    }
}

export function* saveProjectIssue({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const allProjectIssues = yield select(getProjectIssueList);
        const selectedProject = yield select(getSelectedProject);
        let finalPayload = { ...payload };
        // check the orgid of the payload - it will be empty for a new payload - populate from the selectedOrg - otherwise leave it as is
        if (payload?.orgid === '') {
            finalPayload = { ...payload, orgid: selectedOrg?.id };
        }
        const savedProjectIssue = yield call(
            addOrUpdateDataRequest,
            allProjectIssues,
            finalPayload,
            'addProjectIssue',
            'updateProjectIssue',
            [ORGID_PATH_PARAM, 'projectId'],
            [selectedOrg?.id, selectedProject?.id]);
        yield put(onSaveProjectIssueSuccess(savedProjectIssue));
    } catch (error) {
        yield put(showProjectIssueMessage(apiErrorMessageFormatter(error)));
    }
}

export function* saveJiraProjectIssue({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const allProjectIssues = yield select(getProjectIssueList);
        let finalPayload = { ...payload };
        // check the orgid of the payload - it will be empty for a new payload - populate from the selectedOrg - otherwise leave it as is
        if (payload?.orgid === '') {
            finalPayload = { ...payload, orgid: selectedOrg?.id };
        }
        const savedProjectIssues = yield call(
            addOrUpdateDataRequest,
            allProjectIssues,
            finalPayload,
            'addProjectIssue',
            'updateProjectIssue',
            [ORGID_PATH_PARAM],
            [selectedOrg.id]);
        yield put(onSaveProjectIssueSuccess(savedProjectIssues));
    } catch (error) {
        yield put(showProjectIssueMessage(apiErrorMessageFormatter(error)));
    }
}

/**
 * This is when the user selects the star on the list page - update the starred attribute of the entity
 * NOTE - we do not store issues in the db so only update the local store which is refereshed as required
 * @param {*} param0 
 */
export function* addFavouriteProjectIssue({ payload }) {
    try {
        const allProjectIssues = yield select(getProjectIssueList);
        const filtered = allProjectIssues.filter((entry) => entry?.id === payload?.id);
        if (filtered?.length > 0) {
            yield put(addFavouriteProjectIssueSuccess(payload));
        }
    } catch (error) {
        yield put(showProjectIssueMessage(error.messages));
    }
}

export function* fetchProjectIssuesSaga() {
    yield takeEvery(FETCH_ALL_PROJECT_ISSUE, fetchProjectIssues);
}

export function* saveProjectIssueSaga() {
    yield takeEvery(ON_SAVE_PROJECT_ISSUE, saveProjectIssue);
}

export function* saveJiraProjectIssueSaga() {
    yield takeEvery(ON_SAVE_JIRA_PROJECT_ISSUE, saveJiraProjectIssue);
}

export function* deleteProjectIssueSaga() {
    yield takeEvery(ON_DELETE_PROJECT_ISSUE, deleteProjectIssue);
}

export function* addFavouriteProjectIssueSaga() {
    yield takeEvery(ADD_FAVOURITE_PROJECT_ISSUE, addFavouriteProjectIssue);
}

export default function* rootSaga() {
    yield all([
        fork(fetchProjectIssuesSaga),
        fork(saveProjectIssueSaga),
        fork(deleteProjectIssueSaga),
        fork(addFavouriteProjectIssueSaga),
    ]);
}


// WEBPACK FOOTER //
// ./sagas/projectIssue.js